import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { API_BASE_URL } from '../../config/api'
import { authFetch } from '../../utils/authFetch'
import {
  ArrowLeft,
  Building2,
  Globe,
  Save,
  AlertCircle,
  CheckCircle2,
  Archive,
  RotateCcw,
  Trash2,
  X,
  MapPin,
  ShoppingBag,
  Calendar,
  Users,
  Loader2,
  Key,
  Shield,
  Palette,
  Package,
  CreditCard
} from 'lucide-react'
import CredentialModal from './CredentialModal'
import BrandingEditor, { BrandingFormState, DEFAULT_BRANDING } from './BrandingEditor'
import BrandingPreview from './BrandingPreview'
import PaymentConfigEditor, { PaymentConfigFormState, DEFAULT_PAYMENT_CONFIG } from './PaymentConfigEditor'

interface OperatorDetailData {
  id: string
  name: string
  email: string
  company: string
  plan: 'starter' | 'professional' | 'enterprise'
  status: 'active' | 'suspended' | 'archived'
  subdomain: string | null
  customDomain: string | null
  platformDomain: string
  joinDate: string | null
  lastLogin: string | null
  stats: { accommodations: number; activities: number; packages: number; bookings: number }
}

interface EditForm {
  name: string
  email: string
  company: string
  plan: string
  status: string
  subdomain: string
}

const STATUS_BADGES: Record<string, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-green-100 text-green-800' },
  suspended: { label: 'Suspended', className: 'bg-yellow-100 text-yellow-800' },
  archived: { label: 'Archived', className: 'bg-gray-100 text-gray-600' }
}

const OperatorDetail: React.FC = () => {
  const { operatorId } = useParams<{ operatorId: string }>()
  const navigate = useNavigate()

  // Core state
  const [operator, setOperator] = useState<OperatorDetailData | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)

  // Editable form fields
  const [editForm, setEditForm] = useState<EditForm>({
    name: '', email: '', company: '', plan: 'starter', status: 'active', subdomain: ''
  })
  const [isDirty, setIsDirty] = useState(false)

  // Subdomain editing
  const [subdomainAvailable, setSubdomainAvailable] = useState<boolean | null>(null)
  const [subdomainCheckMessage, setSubdomainCheckMessage] = useState('')
  const [checkingSubdomain, setCheckingSubdomain] = useState(false)
  const subdomainTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Archive confirmation
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  const [archiveConfirmText, setArchiveConfirmText] = useState('')
  const [archiving, setArchiving] = useState(false)

  // Restore
  const [restoring, setRestoring] = useState(false)

  // Credential management
  const [hasCredentials, setHasCredentials] = useState<boolean | null>(null)
  const [generatingCredentials, setGeneratingCredentials] = useState(false)
  const [credentials, setCredentials] = useState<{ email: string; password: string } | null>(null)
  const [emailQueued, setEmailQueued] = useState(false)
  const [showCredentialModal, setShowCredentialModal] = useState(false)
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const [lastActionWasReset, setLastActionWasReset] = useState(false)

  // Branding state
  const [brandingForm, setBrandingForm] = useState<BrandingFormState>(DEFAULT_BRANDING)
  const [brandingDirty, setBrandingDirty] = useState(false)
  const [savingBranding, setSavingBranding] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)

  // Payment config state
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfigFormState>(DEFAULT_PAYMENT_CONFIG)
  const [paymentConfigDirty, setPaymentConfigDirty] = useState(false)
  const [savingPaymentConfig, setSavingPaymentConfig] = useState(false)
  const [paymentModeWarning, setPaymentModeWarning] = useState<string | null>(null)

  // Auto-dismiss success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  // Load operator data
  const loadOperator = useCallback(async () => {
    if (!operatorId) return
    try {
      setLoading(true)
      setError(null)
      const response = await authFetch(`${API_BASE_URL}/api/super-admin/operators/${operatorId}`)
      if (response.status === 404) {
        setNotFound(true)
        return
      }
      if (!response.ok) {
        throw new Error('Failed to load operator details')
      }
      const data = await response.json()
      if (data.success && data.operator) {
        const op = data.operator as OperatorDetailData
        setOperator(op)
        setEditForm({
          name: op.name || '',
          email: op.email || '',
          company: op.company || '',
          plan: op.plan || 'starter',
          status: op.status === 'archived' ? 'active' : (op.status || 'active'),
          subdomain: op.subdomain || ''
        })
        setIsDirty(false)
        setSubdomainAvailable(null)
        setSubdomainCheckMessage('')

        // Load branding settings
        try {
          const brandingResponse = await authFetch(
            `${API_BASE_URL}/api/super-admin/operators/${operatorId}/branding`
          )
          if (brandingResponse.ok) {
            const brandingData = await brandingResponse.json()
            if (brandingData.success && brandingData.branding) {
              const b = brandingData.branding
              setBrandingForm({
                primaryColor: b.primary_color || DEFAULT_BRANDING.primaryColor,
                secondaryColor: b.secondary_color || DEFAULT_BRANDING.secondaryColor,
                accentColor: b.accent_color || DEFAULT_BRANDING.accentColor,
                textColor: b.text_color || DEFAULT_BRANDING.textColor,
                backgroundColor: b.background_color || DEFAULT_BRANDING.backgroundColor,
                logoUrl: b.logo_url || null,
              })
            }
          }
        } catch {
          // Non-critical -- use defaults
        }
        setBrandingDirty(false)

        // Load payment config
        try {
          const paymentResponse = await authFetch(
            `${API_BASE_URL}/api/super-admin/operators/${operatorId}/payment-config`
          )
          if (paymentResponse.ok) {
            const paymentData = await paymentResponse.json()
            if (paymentData.success && paymentData.config) {
              const pc = paymentData.config
              setPaymentConfig({
                isEnabled: pc.is_active ?? false,
                gatewayType: pc.gateway_type || 'stripe_connect',
                publishableKey: pc.publishable_key || '',
                secretKey: pc.secret_key || '',
                testMode: pc.test_mode ?? true,
                stripeAccountId: pc.stripe_account_id || '',
              })
              setPaymentModeWarning(paymentData.mode_warning || null)
            }
          }
        } catch {
          // Non-critical -- use defaults
        }
        setPaymentConfigDirty(false)
      }

      // Check credential status
      try {
        const credResponse = await authFetch(
          `${API_BASE_URL}/api/super-admin/operators/${operatorId}/credential-status`
        )
        if (credResponse.ok) {
          const credData = await credResponse.json()
          setHasCredentials(credData.has_credentials)
        }
      } catch {
        // Non-critical -- default to unknown
        setHasCredentials(null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load operator')
    } finally {
      setLoading(false)
    }
  }, [operatorId])

  useEffect(() => {
    loadOperator()
  }, [loadOperator])

  // Track dirty state
  const handleFormChange = (field: keyof EditForm, value: string) => {
    setEditForm(prev => ({ ...prev, [field]: value }))
    setIsDirty(true)
    if (field === 'subdomain') {
      checkSubdomainAvailability(value)
    }
  }

  // Debounced subdomain availability check
  const checkSubdomainAvailability = useCallback((slug: string) => {
    if (subdomainTimerRef.current) {
      clearTimeout(subdomainTimerRef.current)
    }

    // If unchanged from original, reset
    if (!operator || slug === (operator.subdomain || '')) {
      setSubdomainAvailable(null)
      setSubdomainCheckMessage('')
      setCheckingSubdomain(false)
      return
    }

    if (!slug || slug.length < 3) {
      setSubdomainAvailable(false)
      setSubdomainCheckMessage('Subdomain must be at least 3 characters')
      setCheckingSubdomain(false)
      return
    }

    setCheckingSubdomain(true)
    subdomainTimerRef.current = setTimeout(async () => {
      try {
        const response = await authFetch(
          `${API_BASE_URL}/api/super-admin/operators/check-subdomain/${encodeURIComponent(slug)}`
        )
        const data = await response.json()
        if (data.success) {
          setSubdomainAvailable(data.available)
          setSubdomainCheckMessage(
            data.available
              ? `"${slug}" is available`
              : (data.reason || `"${slug}" is already taken`)
          )
        }
      } catch {
        setSubdomainCheckMessage('Could not check availability')
        setSubdomainAvailable(null)
      } finally {
        setCheckingSubdomain(false)
      }
    }, 500)
  }, [operator])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (subdomainTimerRef.current) {
        clearTimeout(subdomainTimerRef.current)
      }
    }
  }, [])

  // Save handler
  const handleSave = async () => {
    if (!operator || !operatorId) return

    // Block save if subdomain is changed and unavailable
    const subdomainChanged = editForm.subdomain !== (operator.subdomain || '')
    if (subdomainChanged && subdomainAvailable === false) {
      setError('Cannot save: the selected subdomain is unavailable')
      return
    }

    try {
      setSaving(true)
      setError(null)

      const body: Record<string, string> = {
        name: editForm.name,
        email: editForm.email,
        company: editForm.company,
        plan: editForm.plan,
        status: editForm.status
      }

      if (subdomainChanged) {
        body.subdomain = editForm.subdomain
      }

      const response = await authFetch(`${API_BASE_URL}/api/super-admin/operators/${operatorId}`, {
        method: 'PUT',
        body: JSON.stringify(body)
      })

      if (response.status === 409) {
        const errData = await response.json()
        setError(errData.detail || 'Subdomain conflict: that subdomain is already taken')
        return
      }

      if (!response.ok) {
        const errData = await response.json().catch(() => ({ detail: 'Failed to save' }))
        setError(errData.detail || 'Failed to save operator')
        return
      }

      const data = await response.json()
      if (data.success) {
        setSuccessMessage('Operator updated successfully')
        // Reload to get fresh data
        await loadOperator()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save operator')
    } finally {
      setSaving(false)
    }
  }

  // Archive handler
  const handleArchive = async () => {
    if (!operatorId) return
    try {
      setArchiving(true)
      setError(null)

      const response = await authFetch(`${API_BASE_URL}/api/super-admin/operators/${operatorId}`, {
        method: 'DELETE'
      })

      if (!response.ok) {
        const errData = await response.json().catch(() => ({ detail: 'Failed to archive' }))
        setError(errData.detail || 'Failed to archive operator')
        return
      }

      const data = await response.json()
      if (data.success) {
        setSuccessMessage('Operator archived successfully. Redirecting...')
        setTimeout(() => navigate('/super-admin/operators'), 2000)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive operator')
    } finally {
      setArchiving(false)
      setShowArchiveConfirm(false)
      setArchiveConfirmText('')
    }
  }

  // Restore handler
  const handleRestore = async () => {
    if (!operatorId) return
    try {
      setRestoring(true)
      setError(null)

      const response = await authFetch(`${API_BASE_URL}/api/super-admin/operators/${operatorId}/restore`, {
        method: 'POST'
      })

      if (!response.ok) {
        const errData = await response.json().catch(() => ({ detail: 'Failed to restore' }))
        setError(errData.detail || 'Failed to restore operator')
        return
      }

      const data = await response.json()
      if (data.success) {
        setSuccessMessage('Operator restored successfully')
        await loadOperator()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to restore operator')
    } finally {
      setRestoring(false)
    }
  }

  // Credential handlers
  const handleGenerateCredentials = async () => {
    if (!operatorId) return
    try {
      setGeneratingCredentials(true)
      setLastActionWasReset(false)
      setError(null)
      const response = await authFetch(
        `${API_BASE_URL}/api/super-admin/operators/${operatorId}/generate-credentials`,
        { method: 'POST' }
      )
      const data = await response.json()
      if (response.status === 400 && data.detail?.includes('already exists')) {
        setHasCredentials(true)
        setError('Admin credentials already exist for this operator. Use "Reset Credentials" to generate new ones.')
        return
      }
      if (!response.ok) {
        setError(data.detail || 'Failed to generate credentials')
        return
      }
      if (data.success && data.credentials) {
        setCredentials({ email: data.credentials.email, password: data.credentials.password })
        setEmailQueued(!!data.email_queued)
        setShowCredentialModal(true)
        setHasCredentials(true)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate credentials')
    } finally {
      setGeneratingCredentials(false)
    }
  }

  const handleResetCredentials = async () => {
    if (!operatorId) return
    try {
      setGeneratingCredentials(true)
      setLastActionWasReset(true)
      setError(null)
      setShowResetConfirm(false)
      const response = await authFetch(
        `${API_BASE_URL}/api/super-admin/operators/${operatorId}/generate-credentials?reset=true`,
        { method: 'POST' }
      )
      const data = await response.json()
      if (!response.ok) {
        setError(data.detail || 'Failed to reset credentials')
        return
      }
      if (data.success && data.credentials) {
        setCredentials({ email: data.credentials.email, password: data.credentials.password })
        setEmailQueued(!!data.email_queued)
        setShowCredentialModal(true)
        setHasCredentials(true)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset credentials')
    } finally {
      setGeneratingCredentials(false)
    }
  }

  const handleCredentialDismiss = () => {
    setShowCredentialModal(false)
    setCredentials(null)
    setEmailQueued(false)
  }

  // Branding change handler
  const handleBrandingChange = (newBranding: BrandingFormState) => {
    setBrandingForm(newBranding)
    setBrandingDirty(true)
  }

  // Save branding handler
  const handleSaveBranding = async () => {
    if (!operatorId) return
    try {
      setSavingBranding(true)
      setError(null)
      const response = await authFetch(
        `${API_BASE_URL}/api/super-admin/operators/${operatorId}/branding`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            primary_color: brandingForm.primaryColor,
            secondary_color: brandingForm.secondaryColor,
            accent_color: brandingForm.accentColor,
            text_color: brandingForm.textColor,
            background_color: brandingForm.backgroundColor,
          })
        }
      )
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.detail || 'Failed to save branding')
      }
      setBrandingDirty(false)
      setSuccessMessage('Branding saved successfully')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save branding')
    } finally {
      setSavingBranding(false)
    }
  }

  // Payment config change handler
  const handlePaymentConfigChange = (newConfig: PaymentConfigFormState) => {
    setPaymentConfig(newConfig)
    setPaymentConfigDirty(true)
  }

  // Save payment config handler
  const handleSavePaymentConfig = async () => {
    if (!operatorId) return
    try {
      setSavingPaymentConfig(true)
      setError(null)
      const response = await authFetch(
        `${API_BASE_URL}/api/super-admin/operators/${operatorId}/payment-config`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            is_active: paymentConfig.isEnabled,
            gateway_type: paymentConfig.gatewayType,
            publishable_key: paymentConfig.publishableKey,
            secret_key: paymentConfig.secretKey,
            test_mode: paymentConfig.testMode,
            stripe_account_id: paymentConfig.stripeAccountId || null,
          })
        }
      )
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to save payment config')
      }
      // Update mode warning from response
      if (data.mode_warning) {
        setPaymentModeWarning(data.mode_warning)
      } else {
        setPaymentModeWarning(null)
      }
      // Update masked secret key from response if returned
      if (data.config?.secret_key) {
        setPaymentConfig(prev => ({ ...prev, secretKey: data.config.secret_key }))
      }
      setPaymentConfigDirty(false)
      setSuccessMessage('Payment configuration saved successfully')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save payment config')
    } finally {
      setSavingPaymentConfig(false)
    }
  }

  // Logo upload handler
  const handleLogoUpload = async (file: File) => {
    if (!operatorId) return
    try {
      setUploadingLogo(true)
      setError(null)
      const formData = new FormData()
      formData.append('file', file)
      const response = await authFetch(
        `${API_BASE_URL}/api/super-admin/operators/${operatorId}/logo`,
        { method: 'POST', body: formData }
      )
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.detail || 'Failed to upload logo')
      }
      const data = await response.json()
      if (data.success && data.logo_url) {
        setBrandingForm(prev => ({ ...prev, logoUrl: data.logo_url }))
        setSuccessMessage('Logo uploaded successfully')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload logo')
    } finally {
      setUploadingLogo(false)
    }
  }

  // Logo remove handler
  const handleLogoRemove = async () => {
    if (!operatorId) return
    try {
      setUploadingLogo(true)
      setError(null)
      const response = await authFetch(
        `${API_BASE_URL}/api/super-admin/operators/${operatorId}/logo`,
        { method: 'DELETE' }
      )
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.detail || 'Failed to remove logo')
      }
      setBrandingForm(prev => ({ ...prev, logoUrl: null }))
      setSuccessMessage('Logo removed')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove logo')
    } finally {
      setUploadingLogo(false)
    }
  }

  const isArchived = operator?.status === 'archived'
  const archiveConfirmMatch = archiveConfirmText === (operator?.company || '')
  const canSave = isDirty && !saving && !(
    editForm.subdomain !== (operator?.subdomain || '') && subdomainAvailable === false
  )

  // --- RENDER ---

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-500">Loading operator details...</span>
      </div>
    )
  }

  // Not found state
  if (notFound) {
    return (
      <div className="text-center py-20">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 mb-2">Operator Not Found</h2>
        <p className="text-gray-500 mb-6">The operator you are looking for does not exist or has been removed.</p>
        <button
          onClick={() => navigate('/super-admin/operators')}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Operators
        </button>
      </div>
    )
  }

  if (!operator) return null

  const statusBadge = STATUS_BADGES[operator.status] || STATUS_BADGES.active

  return (
    <div className="space-y-6">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/super-admin/operators')}
            className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{operator.company}</h1>
              <p className="text-sm text-gray-500">{operator.email}</p>
              <p className="text-xs text-gray-400 font-mono mt-0.5">ID: {operator.id}</p>
            </div>
          </div>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.className}`}>
            {statusBadge.label}
          </span>
        </div>

        {/* Save button - hidden when archived */}
        {!isArchived && (
          <button
            onClick={handleSave}
            disabled={!canSave}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              canSave
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        )}
      </div>

      {/* Archived banner */}
      {isArchived && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
          <Archive className="w-5 h-5 text-yellow-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-yellow-800">This operator is archived</p>
            <p className="text-sm text-yellow-700">All fields are read-only. Restore the operator to make changes.</p>
          </div>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Success banner */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-800">{successMessage}</p>
          </div>
          <button onClick={() => setSuccessMessage(null)} className="text-green-400 hover:text-green-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Operator info form */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Operator Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input
                  type="text"
                  value={editForm.company}
                  onChange={(e) => handleFormChange('company', e.target.value)}
                  disabled={isArchived}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                  placeholder="Company name"
                />
              </div>

              {/* Contact Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  disabled={isArchived}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                  placeholder="Contact name"
                />
              </div>

              {/* Admin Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  disabled={isArchived}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                  placeholder="admin@example.com"
                />
              </div>

              {/* Plan */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                <select
                  value={editForm.plan}
                  onChange={(e) => handleFormChange('plan', e.target.value)}
                  disabled={isArchived}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                >
                  <option value="starter">Starter</option>
                  <option value="professional">Professional</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={isArchived ? 'archived' : editForm.status}
                  onChange={(e) => handleFormChange('status', e.target.value)}
                  disabled={isArchived}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                >
                  {isArchived ? (
                    <option value="archived">Archived</option>
                  ) : (
                    <>
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                    </>
                  )}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Subdomain section */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Subdomain</h2>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subdomain Slug</label>
                <div className="relative">
                  <input
                    type="text"
                    value={editForm.subdomain}
                    onChange={(e) => handleFormChange('subdomain', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    disabled={isArchived}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                    placeholder="my-company"
                  />
                  {checkingSubdomain && (
                    <div className="absolute right-3 top-2.5">
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    </div>
                  )}
                  {!checkingSubdomain && subdomainAvailable === true && (
                    <div className="absolute right-3 top-2.5">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    </div>
                  )}
                  {!checkingSubdomain && subdomainAvailable === false && (
                    <div className="absolute right-3 top-2.5">
                      <X className="w-4 h-4 text-red-500" />
                    </div>
                  )}
                </div>
              </div>

              {/* Availability message */}
              {subdomainCheckMessage && (
                <p className={`text-xs ${subdomainAvailable ? 'text-green-600' : 'text-red-600'}`}>
                  {subdomainCheckMessage}
                </p>
              )}

              {/* Full URL preview */}
              <div className="bg-gray-50 rounded-md px-3 py-2">
                <p className="text-xs text-gray-500 mb-1">Full URL Preview</p>
                <p className="text-sm font-mono text-gray-800">
                  {editForm.subdomain || 'your-subdomain'}.{operator.platformDomain}
                </p>
              </div>

              {/* Custom domain info */}
              {operator.customDomain && (
                <div className="text-xs text-gray-500">
                  <span className="font-medium">Custom domain:</span> {operator.customDomain}
                </div>
              )}
            </div>
          </div>

          {/* Stats section */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Statistics</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <MapPin className="w-4 h-4" />
                  <span>Accommodations</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">{operator.stats.accommodations}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <ShoppingBag className="w-4 h-4" />
                  <span>Activities</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">{operator.stats.activities}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Package className="w-4 h-4" />
                  <span>Packages</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">{operator.stats.packages}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>Bookings</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">{operator.stats.bookings}</span>
              </div>
              <hr className="border-gray-100" />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Users className="w-4 h-4" />
                  <span>Joined</span>
                </div>
                <span className="text-sm text-gray-700">
                  {operator.joinDate ? new Date(operator.joinDate).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>Last Updated</span>
                </div>
                <span className="text-sm text-gray-700">
                  {operator.lastLogin ? new Date(operator.lastLogin).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Credentials section */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Key className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Admin Credentials</h2>
            </div>

            {/* Credential status indicator */}
            {hasCredentials === true && (
              <div className="flex items-center gap-2 mb-4 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-md">
                <Shield className="w-4 h-4" />
                <span>Credentials have been generated</span>
              </div>
            )}

            <div className="space-y-2">
              {/* Generate button -- for operators without credentials */}
              <button
                onClick={handleGenerateCredentials}
                disabled={generatingCredentials || isArchived}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generatingCredentials ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Key className="w-4 h-4" />
                )}
                {generatingCredentials ? 'Generating...' : 'Generate Credentials'}
              </button>

              {/* Reset button -- for operators that may have existing credentials */}
              {!showResetConfirm ? (
                <button
                  onClick={() => setShowResetConfirm(true)}
                  disabled={generatingCredentials || isArchived}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset Credentials
                </button>
              ) : (
                <div className="border border-yellow-200 rounded-lg p-4 bg-yellow-50 space-y-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Reset credentials?</p>
                      <p className="text-xs text-yellow-700 mt-1">
                        This will invalidate the current admin password and generate a new one. The operator will need to use the new credentials.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleResetCredentials}
                      disabled={generatingCredentials}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-yellow-600 text-white rounded-md text-sm font-medium hover:bg-yellow-700 transition-colors disabled:opacity-50"
                    >
                      {generatingCredentials ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RotateCcw className="w-4 h-4" />
                      )}
                      {generatingCredentials ? 'Resetting...' : 'Confirm Reset'}
                    </button>
                    <button
                      onClick={() => setShowResetConfirm(false)}
                      className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {isArchived && (
              <p className="text-xs text-gray-500 mt-3">Restore the operator to manage credentials.</p>
            )}
          </div>

          {/* Actions section */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>

            {isArchived ? (
              /* Restore button for archived operators */
              <button
                onClick={handleRestore}
                disabled={restoring}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {restoring ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RotateCcw className="w-4 h-4" />
                )}
                {restoring ? 'Restoring...' : 'Restore Operator'}
              </button>
            ) : (
              /* Archive button for active/suspended operators */
              <div className="space-y-3">
                {!showArchiveConfirm ? (
                  <button
                    onClick={() => setShowArchiveConfirm(true)}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-md text-sm font-medium hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Archive Operator
                  </button>
                ) : (
                  /* Inline archive confirmation */
                  <div className="border border-red-200 rounded-lg p-4 bg-red-50 space-y-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-800">Archive this operator?</p>
                        <p className="text-xs text-red-700 mt-1">
                          This will set the operator status to archived. The operator will not be able to access their account.
                          Data is preserved and can be restored later.
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-red-800 mb-1">
                        Type <span className="font-bold">"{operator.company}"</span> to confirm
                      </label>
                      <input
                        type="text"
                        value={archiveConfirmText}
                        onChange={(e) => setArchiveConfirmText(e.target.value)}
                        className="w-full px-3 py-2 border border-red-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder={operator.company}
                        autoFocus
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleArchive}
                        disabled={!archiveConfirmMatch || archiving}
                        className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          archiveConfirmMatch && !archiving
                            ? 'bg-red-600 text-white hover:bg-red-700'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        {archiving ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Archive className="w-4 h-4" />
                        )}
                        {archiving ? 'Archiving...' : 'Confirm Archive'}
                      </button>
                      <button
                        onClick={() => {
                          setShowArchiveConfirm(false)
                          setArchiveConfirmText('')
                        }}
                        className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Branding Customization Section */}
      {operator && (
        <div className="mt-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Palette className="w-5 h-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Branding Customization</h2>
              </div>
              {brandingDirty && !isArchived && (
                <button
                  onClick={handleSaveBranding}
                  disabled={savingBranding}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {savingBranding ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {savingBranding ? 'Saving...' : 'Save Branding'}
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Editor - 2 columns on desktop */}
              <div className="lg:col-span-2">
                <BrandingEditor
                  branding={brandingForm}
                  onChange={handleBrandingChange}
                  onLogoUpload={handleLogoUpload}
                  onLogoRemove={handleLogoRemove}
                  uploadingLogo={uploadingLogo}
                  disabled={isArchived}
                />
              </div>

              {/* Preview - 1 column on desktop */}
              <div>
                <div className="sticky top-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Live Preview</h3>
                  <BrandingPreview
                    branding={brandingForm}
                    operatorName={operator.company}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Configuration Section */}
      {operator && (
        <div className="mt-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Payment Configuration</h2>
              </div>
              {paymentConfigDirty && !isArchived && (
                <button
                  onClick={handleSavePaymentConfig}
                  disabled={savingPaymentConfig}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {savingPaymentConfig ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {savingPaymentConfig ? 'Saving...' : 'Save Payment Config'}
                </button>
              )}
            </div>

            <PaymentConfigEditor
              config={paymentConfig}
              onChange={handlePaymentConfigChange}
              modeWarning={paymentModeWarning}
              disabled={isArchived}
            />
          </div>
        </div>
      )}

      {/* Credential Modal */}
      {showCredentialModal && (
        <CredentialModal
          credentials={credentials}
          emailQueued={emailQueued}
          isReset={lastActionWasReset}
          onDismiss={handleCredentialDismiss}
        />
      )}
    </div>
  )
}

export default OperatorDetail
